import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { TOURS, ARTICLES, FAQS } from "../../mock";
import SEO from "../../seo-routes.json";

// Head-only SEO per page (titles, descriptions, canonical). Nothing visible changes.
const SITE = SEO.site;
const BRAND = SEO.brand;
const HOME_TITLE = SEO.pages["/"].title;
const HOME_DESC = SEO.pages["/"].description;
const PAGES = Object.fromEntries(Object.entries(SEO.pages).map(([k, v]) => [k, [v.title, v.description]]));

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!data) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
};

const RouteSeo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    let [title, desc] = PAGES[pathname] || [];
    const tour = pathname.startsWith("/tours/") && TOURS.find((t) => `/tours/${t.slug}` === pathname);
    const art = pathname.startsWith("/corfu-guide/") && ARTICLES.find((a) => `/corfu-guide/${a.slug}` === pathname);
    if (tour) { title = `${tour.title} – Private Corfu Tour | ${BRAND}`; desc = tour.desc.slice(0, 155); }
    if (art) { title = `${art.title} | ${BRAND}`; desc = art.excerpt; }
    if (pathname.startsWith("/legal/") && !title) { [title, desc] = PAGES["/legal/privacy"]; }
    if (pathname === "/admin") { title = `Admin | ${BRAND}`; desc = HOME_DESC; }
    if (pathname === "/transfers/airport") { [title, desc] = PAGES["/airport-transfers"]; }
    title = title || HOME_TITLE; desc = desc || HOME_DESC;

    const canonPath = pathname === "/transfers/airport" ? "/airport-transfers" : pathname;
    const url = `${SITE}${canonPath === "/" ? "/" : canonPath}`;
    document.title = title;
    setMeta("name", "description", desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:site_name", BRAND);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "robots", pathname === "/admin" ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = url;

    setJsonLd("ld-faq", pathname === "/faq" ? {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    } : null);
    setJsonLd("ld-breadcrumb", pathname !== "/" ? {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: BRAND, item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: title.split(" | ")[0], item: url },
      ],
    } : null);
    setJsonLd("ld-webpage", {
      "@context": "https://schema.org", "@type": "WebPage", "@id": `${url}#webpage`, url, name: title, description: desc,
      isPartOf: { "@id": `${SITE}/#website` }, publisher: { "@type": "Organization", name: "Aloha Corfu Transfers", logo: `${SITE}/logo.png` },
    });
  }, [pathname]);
  return null;
};

export default RouteSeo;
